import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Snackbar,
  Tooltip,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,

} from '@mui/material';
import ResponsiveDialog from './ResponsiveDialog';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Hotel as HotelIcon,
  Festival as FestivalIcon,
  Flight as FlightIcon,
  Train as TrainIcon,
  Home as CampingIcon,
} from '@mui/icons-material';
import apiService from '../services/api';
import IconSelector from './IconSelector';
import CategoryIcon from './CategoryIcon';

interface ProviderType {
  id: number;
  name: string;
  slug: string;
  icon?: string | null;
  icon_svg_filename?: string | null;
  icon_svg_url?: string | null;
  description: string;
  /** @deprecated Les lieux se gèrent sur chaque partenaire */
  item_name?: string | null;
  default_attributes: Record<string, any>;
  is_active: boolean;
  annual_event?: boolean;
  place_storage_mode?: 'combined' | 'split' | 'central';
  sort_order: number;
  created_at: string;
  updated_at?: string;
  location_providers_count?: number;
  provider_items_count?: number;
}

interface ProviderTypeFormData {
  name: string;
  description: string;
  icon: string;
  /** Fichier présent dans uploads/pictos (upload API ou bibliothèque). */
  icon_svg_filename: string;
  default_attributes: Record<string, any>;
  sort_order: number;
  is_active: boolean;
  annual_event: boolean;
  place_storage_mode: 'combined' | 'split' | 'central'
}

const iconMap: Record<string, React.ReactElement> = {
  'hotel': <HotelIcon />,
  'camping': <CampingIcon />,
  'festival': <FestivalIcon />,
  'gare': <TrainIcon />,
  'aeroport': <FlightIcon />,
};

const ProviderTypeManager: React.FC = () => {
  const [providerTypes, setProviderTypes] = useState<ProviderType[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingType, setEditingType] = useState<ProviderType | null>(null);
  const [iconSelectorOpen, setIconSelectorOpen] = useState(false);
  const [formData, setFormData] = useState<ProviderTypeFormData>({
    name: '',
    description: '',
    icon: '',
    icon_svg_filename: '',
    default_attributes: {},
    sort_order: 0,
    is_active: true,
    annual_event: false,
    place_storage_mode: 'combined',
  });
  const pictoFileInputRef = useRef<HTMLInputElement>(null);
  const [pictoUploading, setPictoUploading] = useState(false);
  const [pictoLibraryOpen, setPictoLibraryOpen] = useState(false);
  const [pictoLibraryLoading, setPictoLibraryLoading] = useState(false);
  const [pictoLibraryItems, setPictoLibraryItems] = useState<Array<{ filename: string; url: string; name: string }>>([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const fetchProviderTypes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/provider-types');
      setProviderTypes(response.data.provider_types || []);
    } catch (error) {
      console.error('Erreur lors du chargement des types de partenaires:', error);
      showSnackbar('Erreur lors du chargement des types de partenaires', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProviderTypes();
  }, [fetchProviderTypes]);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (type?: ProviderType) => {
    if (type) {
      setEditingType(type);
      setFormData({
        name: type.name,
        description: type.description || '',
        icon: type.icon || '',
        icon_svg_filename: type.icon_svg_filename || '',
        default_attributes: type.default_attributes,
        sort_order: type.sort_order,
        is_active: type.is_active,
        annual_event: Boolean(type.annual_event),
        place_storage_mode:
          type.place_storage_mode === 'split' || type.place_storage_mode === 'central'
            ? type.place_storage_mode
            : 'combined',
      });
    } else {
      setEditingType(null);
      setFormData({
        name: '',
        description: '',
        icon: '',
        icon_svg_filename: '',
        default_attributes: {},
        sort_order: 0,
        is_active: true,
        annual_event: false,
        place_storage_mode: 'combined',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingType(null);
  };

  const handleSubmit = async () => {
    try {
      const trimmedIcon = formData.icon.trim();
      const payload = {
        ...formData,
        item_name: null as string | null,
        icon: trimmedIcon !== '' ? trimmedIcon : null,
        icon_svg_filename: formData.icon_svg_filename.trim() !== '' ? formData.icon_svg_filename.trim() : null,
      };
      if (editingType) {
        await apiService.put(`/provider-types/${editingType.id}`, payload);
        showSnackbar('Type de partenaire mis à jour avec succès', 'success');
      } else {
        await apiService.post('/provider-types', payload);
        showSnackbar('Type de partenaire créé avec succès', 'success');
      }
      handleCloseDialog();
      fetchProviderTypes();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showSnackbar('Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce type de partenaire ?')) {
      try {
        await apiService.delete(`/provider-types/${id}`);
        showSnackbar('Type de partenaire supprimé avec succès', 'success');
        fetchProviderTypes();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        showSnackbar('Erreur lors de la suppression', 'error');
      }
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
        await apiService.patch(`/provider-types/${id}/toggle-status`);
      showSnackbar('Statut mis à jour avec succès', 'success');
      fetchProviderTypes();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      showSnackbar('Erreur lors de la mise à jour du statut', 'error');
    }
  };

  const getIcon = (slug: string) => {
    return iconMap[slug] || <HotelIcon />;
  };

  const renderTypeIcon = (type: ProviderType) => {
    if (type.icon_svg_filename) {
      return (
        <CategoryIcon
          icon={type.icon || type.slug || 'category'}
          customIconFilename={type.icon_svg_filename}
          size="small"
        />
      );
    }
    if (type.icon) {
      return <CategoryIcon icon={type.icon} size="small" />;
    }
    return getIcon(type.slug);
  };

  const handleIconSelected = (iconName: string) => {
    if (!iconName) return;
    setFormData((prev) => ({ ...prev, icon: iconName }));
    setIconSelectorOpen(false);
  };

  const formIconPreview = () => {
    if (formData.icon_svg_filename) {
      return (
        <CategoryIcon
          icon={formData.icon || editingType?.slug || 'category'}
          customIconFilename={formData.icon_svg_filename}
          size="medium"
        />
      );
    }
    if (formData.icon) {
      return <CategoryIcon icon={formData.icon} size="medium" />;
    }
    if (editingType) {
      return getIcon(editingType.slug);
    }
    return <HotelIcon sx={{ fontSize: 32, color: 'text.secondary' }} />;
  };

  const handlePictoSvgFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !editingType?.id) return;
    setPictoUploading(true);
    try {
      const res = await apiService.uploadProviderTypePictoSvg(editingType.id, file);
      const fn = (res.data as { icon_svg_filename?: string | null })?.icon_svg_filename ?? '';
      setFormData((prev) => ({ ...prev, icon_svg_filename: fn || '' }));
      setEditingType((prev) => (prev ? { ...prev, icon_svg_filename: fn || null, icon_svg_url: fn ? `/uploads/pictos/${fn}` : null } : prev));
      showSnackbar('Picto SVG enregistré', 'success');
      fetchProviderTypes();
    } catch (e) {
      console.error(e);
      showSnackbar("Impossible d'envoyer le SVG (vérifiez le format et la taille).", 'error');
    } finally {
      setPictoUploading(false);
    }
  };

  const handleRemovePictoSvg = async () => {
    if (!editingType?.id || !formData.icon_svg_filename) return;
    try {
      await apiService.deleteProviderTypePictoSvg(editingType.id);
      setFormData((prev) => ({ ...prev, icon_svg_filename: '' }));
      setEditingType((prev) => (prev ? { ...prev, icon_svg_filename: null, icon_svg_url: null } : prev));
      showSnackbar('Picto SVG retiré', 'success');
      fetchProviderTypes();
    } catch (e) {
      console.error(e);
      showSnackbar('Erreur lors de la suppression du picto', 'error');
    }
  };

  const openPictoLibrary = async () => {
    setPictoLibraryOpen(true);
    setPictoLibraryLoading(true);
    try {
      const data = await apiService.getIcons();
      setPictoLibraryItems(Array.isArray(data.icons) ? data.icons : []);
    } catch (e) {
      console.error(e);
      showSnackbar('Impossible de charger la bibliothèque de pictos', 'error');
      setPictoLibraryItems([]);
    } finally {
      setPictoLibraryLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Chargement...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Gestion des Types de Partenaires
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Ajouter un Type
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Icône</TableCell>
                  <TableCell>Nom</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Évén. annuel</TableCell>
                  <TableCell>Lieux / stockage</TableCell>
                  <TableCell>Ordre</TableCell>
                  <TableCell>Utilisations</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {providerTypes.map((type) => (
                  <TableRow key={type.id}>
                    <TableCell>
                      <Tooltip
                        title={
                          type.icon_svg_filename
                            ? `Picto SVG : ${type.icon_svg_filename}`
                            : type.icon
                              ? `Icône : ${type.icon}`
                              : type.name
                        }
                      >
                        <Box component="span" display="inline-flex" alignItems="center">
                          {renderTypeIcon(type)}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {type.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {type.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {type.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={type.is_active ? 'Actif' : 'Inactif'}
                        color={type.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {type.annual_event ? (
                        <Chip label="Oui" color="secondary" size="small" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          type.place_storage_mode === 'central'
                            ? 'Central'
                            : type.place_storage_mode === 'split'
                              ? 'Séparés'
                              : 'Combinés'
                        }
                        size="small"
                        color={
                          type.place_storage_mode === 'central'
                            ? 'success'
                            : type.place_storage_mode === 'split'
                              ? 'info'
                              : 'default'
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{type.sort_order}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {type.location_providers_count || 0} partenaires
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {type.provider_items_count || 0} éléments
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleStatus(type.id)}
                        color={type.is_active ? 'default' : 'primary'}
                      >
                        {type.is_active ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(type)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(type.id)}
                        color="error"
                        disabled={Boolean(type.location_providers_count && type.location_providers_count > 0)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog pour créer/éditer un type */}
      <ResponsiveDialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingType ? 'Modifier le type de partenaire' : 'Nouveau type de partenaire'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 1 }}>
                Les <strong>lieux</strong> (Chambre 1, Restaurant, Bar…) se gèrent sur chaque partenaire dans
                « Partenaires » → modifier → section <strong>Lieux / sous-espaces</strong>.
              </Alert>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom du type"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>
                Icône
              </Typography>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  {formIconPreview()}
                </Box>
                <Button variant="outlined" size="small" onClick={() => setIconSelectorOpen(true)}>
                  Icônes Material (MUI)
                </Button>
                {formData.icon ? (
                  <Button
                    size="small"
                    onClick={() => setFormData({ ...formData, icon: '' })}
                  >
                    Réinitialiser
                  </Button>
                ) : null}
              </Box>
              <TextField
                fullWidth
                size="small"
                sx={{ mt: 1.5 }}
                label="Icône (texte)"
                placeholder="fa-solid fa-film"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                helperText={
                  <>
                    <strong>Font Awesome Free</strong> : classes complètes, ex.{' '}
                    <code>fa-solid fa-film</code> (cinéma), <code>fa-solid fa-masks-theater</code> (théâtre).{' '}
                    Sinon un nom d’icône du sélecteur Material (ex. <code>train</code>) ou vide pour l’icône par défaut selon le slug.
                  </>
                }
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Sans valeur, l’affichage reste basé sur le slug (ex. hôtel, gare).
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Picto SVG personnalisé
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Affiché en priorité sur l’icône Material / Font Awesome. Fichiers stockés dans{' '}
                <code>public/uploads/pictos</code> (SVG validé côté serveur, max 4&nbsp;Mo).
              </Typography>
              <input
                ref={pictoFileInputRef}
                type="file"
                accept=".svg,image/svg+xml"
                hidden
                onChange={handlePictoSvgFile}
              />
              <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!editingType?.id || pictoUploading}
                  onClick={() => pictoFileInputRef.current?.click()}
                >
                  {pictoUploading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                  Importer un SVG
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!editingType?.id}
                  onClick={() => void openPictoLibrary()}
                >
                  Bibliothèque de pictos
                </Button>
                {formData.icon_svg_filename ? (
                  <Button variant="text" size="small" color="warning" onClick={() => void handleRemovePictoSvg()}>
                    Retirer le picto
                  </Button>
                ) : null}
              </Box>
              {!editingType?.id ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Créez d’abord le type, puis rouvrez la fiche pour importer ou choisir un picto.
                </Typography>
              ) : formData.icon_svg_filename ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                  Fichier actuel : <code>{formData.icon_svg_filename}</code>
                </Typography>
              ) : null}
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Ordre d'affichage"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Actif"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Gestion lieux / stockage</InputLabel>
                <Select
                  label="Gestion lieux / stockage"
                  value={formData.place_storage_mode}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      place_storage_mode: e.target.value as 'combined' | 'split' | 'central',
                    })
                  }
                >
                  <MenuItem value="combined">Combinés — boîtes par lieu (festivals, événements)</MenuItem>
                  <MenuItem value="split">Séparés — lieux de perte + stockage central</MenuItem>
                  <MenuItem value="central">Central — stockage unique + boîtes (hôtel, cinéma)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.annual_event}
                    onChange={(e) => setFormData({ ...formData, annual_event: e.target.checked })}
                  />
                }
                label="Événement annuel (festivals, manif. sportives…)"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                Permet des éditions par année, filtrage des objets trouvés par événement côté public.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingType ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <IconSelector
        open={iconSelectorOpen}
        onClose={() => setIconSelectorOpen(false)}
        onSelect={handleIconSelected}
        currentIcon={formData.icon || undefined}
      />

      <ResponsiveDialog open={pictoLibraryOpen} onClose={() => setPictoLibraryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Pictos SVG déjà importés</DialogTitle>
        <DialogContent>
          {pictoLibraryLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : pictoLibraryItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Aucun fichier dans la bibliothèque. Utilisez « Importer un SVG » ou l’upload d’icônes générique.
            </Typography>
          ) : (
            <List dense>
              {pictoLibraryItems.map((item) => (
                <ListItemButton
                  key={item.filename}
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, icon_svg_filename: item.filename }));
                    setPictoLibraryOpen(false);
                    showSnackbar('Picto sélectionné — cliquez sur « Mettre à jour » pour enregistrer.', 'info');
                  }}
                >
                  <ListItemText primary={item.filename} secondary={item.name} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPictoLibraryOpen(false)}>Fermer</Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProviderTypeManager;
